import { t } from '@lingui/core/macro';
import {
    assertPresent,
    isEqual,
    isPresent,
    parseOptionsValuesOrFunc,
    throwErrorUnlessProduction,
} from '@luminovo/commons';
import {
    CheckableChip,
    Chip,
    colorSystem,
    Flexbox,
    SearchInput,
    SecondaryButton,
    TertiaryButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { formatDecimal, formatToLongDate, transEnum } from '@luminovo/frontend-utils';
import { shallow, useSelector } from '@tanstack/react-store';
import { BookmarkIcon, FileSpreadsheetIcon } from 'lucide-react';
import React from 'react';

import { isFilterValue } from '../Filters/createFilterValueUpdater';
import { formatRelativeDateFilterLabel } from '../Filters/DateFilters';
import { Column, ColumnFilter, RowData, Table } from '../tanstackTypes';
import {
    FilterFnOptionTranslations,
    getFilterConfig,
    RelativeDateFilterValue,
    SupportedFilterFn,
    TanStackTableProps,
} from '../type';
import { arrayToggleItem } from '../utils';
import { ColumFilterMenu, FilterMenu, FilterMenuState } from './FilterMenu';
import { SortMenu } from './SortMenu';
import { handleExportAsExcel, VisibilityMenu } from './VisibilityMenu';

const TANSTACK_MENU_BAR_HEIGHT = 44;
const TANSTACK_FILTER_TAG_HEIGHT = 32;

function extractVisibility<TData extends RowData, TSharedContext>(
    enableMenuBar: TanStackTableProps<TData, TSharedContext>['enableMenuBar'],
    table: Table<TData>,
) {
    const sharedContext = table.options.meta?.sharedContext as TSharedContext;

    const visibility = parseOptionsValuesOrFunc(enableMenuBar, {
        table,
        sharedContext,
    });

    const defaultVisibility = {
        globalSearch: true,
        quickFilters: true,
        resultCount: true,
        controlButtons: true,
        exportExcelButton: false,
        actionButton: true,
        filterChips: true,
    };

    if (typeof visibility === 'boolean') {
        return {
            globalSearch: visibility,
            quickFilters: visibility,
            resultCount: visibility,
            controlButtons: visibility,
            exportExcelButton: visibility,
            actionButton: visibility,
            filterChips: visibility,
        };
    }

    if (typeof visibility === 'object' && visibility !== null) {
        return {
            globalSearch: visibility.globalSearch ?? defaultVisibility.globalSearch,
            quickFilters: visibility.quickFilters ?? defaultVisibility.quickFilters,
            resultCount: visibility.resultCount ?? defaultVisibility.resultCount,
            controlButtons: visibility.controlButtons ?? defaultVisibility.controlButtons,
            exportExcelButton: visibility.exportExcelButton ?? defaultVisibility.exportExcelButton,
            actionButton: visibility.actionButton ?? defaultVisibility.actionButton,
            filterChips: visibility.filterChips ?? defaultVisibility.filterChips,
        };
    }

    return defaultVisibility;
}

export function calculateBoxHeight<TData extends RowData, TSharedContext>(
    table: Table<TData>,
    enableMenuBar: TanStackTableProps<TData, TSharedContext>['enableMenuBar'],
    hasFilters: boolean,
): string {
    const visibility = extractVisibility(enableMenuBar, table);
    const { filterChips: _filterChips, ...toolbarVisibility } = visibility;
    const isMenuBarVisible = Object.values(toolbarVisibility).some((v) => v);
    const showFilterChips = visibility.filterChips && hasFilters;

    return `max(200px, calc(100% - ${isMenuBarVisible ? TANSTACK_MENU_BAR_HEIGHT : 0}px - ${showFilterChips ? TANSTACK_FILTER_TAG_HEIGHT : 0}px))`;
}

export function TanStackMenuBar<TData extends RowData, TSharedContext>({
    table,
    enableMenuBar,
    MenuBarTitle,
    ActionButton,
    enableFloatingSelectionBar = false,
}: TanStackTableProps<TData, TSharedContext> & { enableFloatingSelectionBar?: boolean }): JSX.Element {
    const visibility = extractVisibility(enableMenuBar, table);
    const { filterChips: _filterChips, ...toolbarVisibility } = visibility;
    const isMenuBarVisible = Object.values(toolbarVisibility).some((v) => v);
    // Pages also render the menu bar on its own, next to the table, so it subscribes to what its menus and count read.
    const { columnFilters } = useSelector(
        table.store,
        ({ rowSelection, columnSizing, columnResizing, ...rest }) => rest,
        { compare: shallow },
    );
    const hasFilters = columnFilters.length > 0;

    const showFilterChips = visibility.filterChips && hasFilters;

    return (
        <Flexbox
            gap={'12px'}
            sx={{
                flexDirection: 'column',
                paddingBottom: isMenuBarVisible || showFilterChips ? '12px' : 0,
            }}
        >
            {isMenuBarVisible && (
                <Toolbar
                    table={table}
                    visibility={visibility}
                    MenuBarTitle={MenuBarTitle}
                    ActionButton={ActionButton}
                    enableFloatingSelectionBar={enableFloatingSelectionBar}
                />
            )}
            {visibility.filterChips && <FilterChipsContainer table={table} />}
        </Flexbox>
    );
}

/**
 * Renders a consumer's slot, such as an `ActionButton`. Slots read the selection and the row models, so it subscribes
 * to every slice but the column sizes, which none shows.
 */
export function TableSlot<TData extends RowData, TSharedContext>({
    Slot,
    table,
    sharedContext,
}: {
    Slot: React.ComponentType<{ table: Table<TData>; sharedContext: TSharedContext }>;
    table: Table<TData>;
    sharedContext: TSharedContext;
}): JSX.Element {
    useSelector(table.store, ({ columnSizing, columnResizing, ...rest }) => rest, { compare: shallow });
    return <Slot table={table} sharedContext={sharedContext} />;
}

function ResultCount<TData extends RowData>({
    table,
    enableFloatingSelectionBar = false,
}: {
    table: Table<TData>;
    enableFloatingSelectionBar?: boolean;
}) {
    // `TanStackMenuBar`'s own subscription re-renders it on the other slices.
    const isSomeRowSelected = useSelector(
        table.store,
        () => table.getIsAllRowsSelected() || table.getIsSomeRowsSelected(),
    );
    const selectedCount = useSelector(table.store, () => table.getSelectedRowModel().rows.length);
    // With the floating selection bar, the count shows there instead.
    if (!enableFloatingSelectionBar && isSomeRowSelected) {
        return (
            <Text variant={'body'} color={colorSystem.neutral[6]} showEllipsis>
                {t`${formatDecimal(selectedCount)} selected`}
            </Text>
        );
    }
    return (
        <Text variant={'body'} color={colorSystem.neutral[6]} showEllipsis>
            {t`Showing ${formatDecimal(table.options.meta?.totalCount ?? table.getRowModel().rows.length)} results`}
        </Text>
    );
}

function Toolbar<TData extends RowData, TSharedContext>({
    table,
    visibility,
    MenuBarTitle,
    ActionButton,
    enableFloatingSelectionBar = false,
}: {
    table: TanStackTableProps<TData, TSharedContext>['table'];
    visibility: ReturnType<typeof extractVisibility>;
    MenuBarTitle: TanStackTableProps<TData, TSharedContext>['MenuBarTitle'];
    ActionButton: TanStackTableProps<TData, TSharedContext>['ActionButton'];
    enableFloatingSelectionBar?: boolean;
}) {
    const sharedContext = table.options.meta?.sharedContext as TSharedContext;
    const { globalFilter, columnFilters } = useSelector(
        table.store,
        (state) => ({ globalFilter: state.globalFilter, columnFilters: state.columnFilters }),
        { compare: shallow },
    );
    const rows = table.getCoreRowModel().rows;

    const quickFilters = table.getAllLeafColumns().flatMap((column) => {
        const quickFilters = column.columnDef.meta?.quickFilters ?? [];

        return quickFilters.map(({ label, value, showCount, replaceExistingFilters, endIcon }) => {
            const filterFn = column.getFilterFn()!;
            const filteredRows = rows.filter((row) =>
                filterFn(row, column.id, value, (filterMeta) => {
                    row.columnFiltersMeta[column.id] = filterMeta;
                }),
            );
            return {
                label,
                replaceExistingFilters,
                count: showCount !== undefined ? filteredRows.length : undefined,
                columnFilter: { id: column.id, value },
                endIcon,
            };
        });
    });

    return (
        <Flexbox
            gap={'8px'}
            sx={{
                alignItems: 'center',
            }}
        >
            {MenuBarTitle && <TableSlot Slot={MenuBarTitle} table={table} sharedContext={sharedContext} />}
            {visibility.globalSearch && (
                <SearchInput
                    debounceWait={50}
                    value={globalFilter}
                    onChange={(value) => table.setGlobalFilter(value)}
                />
            )}
            <Flexbox
                sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                }}
            >
                {visibility.quickFilters && (
                    <Flexbox
                        gap={'8px'}
                        style={{ minWidth: 0 }}
                        sx={{
                            padding: '2px',
                        }}
                    >
                        {quickFilters.map((quickFilter, idx) => {
                            const formattedCount =
                                quickFilter.count !== undefined ? formatDecimal(quickFilter.count) : undefined;

                            const label = [formattedCount, quickFilter.label()]
                                .filter((x) => x !== undefined)
                                .join(' ');
                            return (
                                <CheckableChip
                                    key={idx}
                                    done={false}
                                    label={label}
                                    endIcon={quickFilter.endIcon}
                                    selected={columnFilters.some((f) => isEqual(f, quickFilter.columnFilter))}
                                    onClick={() => {
                                        if (quickFilter.replaceExistingFilters) {
                                            table.setColumnFilters((old) => {
                                                const isAlreadySet = old.some((filter) =>
                                                    isEqual(filter, quickFilter.columnFilter),
                                                );

                                                if (isAlreadySet) {
                                                    return old.filter(
                                                        (filter) => !isEqual(filter, quickFilter.columnFilter),
                                                    );
                                                } else {
                                                    return [quickFilter.columnFilter];
                                                }
                                            });
                                        } else {
                                            table.setColumnFilters((old) =>
                                                arrayToggleItem(old, quickFilter.columnFilter),
                                            );
                                        }
                                    }}
                                    showEllipsis={true}
                                />
                            );
                        })}
                    </Flexbox>
                )}
            </Flexbox>
            <Flexbox
                gap={'8px'}
                sx={{
                    alignItems: 'center',
                }}
            >
                {visibility.resultCount && !table.options.meta?.getIsLoading() && (
                    <ResultCount table={table} enableFloatingSelectionBar={enableFloatingSelectionBar} />
                )}
                {visibility.controlButtons && (
                    <>
                        <FilterMenu table={table} />
                        <SortMenu table={table} />
                        <VisibilityMenu table={table} />
                    </>
                )}
                {visibility.exportExcelButton && (
                    <SecondaryButton
                        startIcon={<FileSpreadsheetIcon color={colorSystem.green[7]} />}
                        size={'medium'}
                        onClick={() => handleExportAsExcel({ table })}
                    >
                        {t`Export view`}
                    </SecondaryButton>
                )}
                {visibility.actionButton && ActionButton && (
                    <TableSlot Slot={ActionButton} table={table} sharedContext={sharedContext} />
                )}
            </Flexbox>
        </Flexbox>
    );
}

function FilterChipsContainer<TData extends RowData>({ table }: TanStackTableProps<TData, unknown>): JSX.Element {
    const { columnFilters, sorting } = useSelector(
        table.store,
        (state) => ({ columnFilters: state.columnFilters, sorting: state.sorting }),
        { compare: shallow },
    );
    const { sessionFiltersAndSorting, enableSaveAsDefault } = assertPresent(table.options.meta);

    const handleClick = () => {
        sessionFiltersAndSorting?.setPersistentFilters(table.store.state.columnFilters);
        sessionFiltersAndSorting?.setPersistentSorting(table.store.state.sorting);
    };

    const handleReset = () => {
        table.setColumnFilters([]);
        table.setSorting([]);
        sessionFiltersAndSorting?.setPersistentFilters([]);
        sessionFiltersAndSorting?.setPersistentSorting([]);
    };
    const hasEqualFiltersAndSorting =
        isEqual(sessionFiltersAndSorting?.persistentFilters, columnFilters) &&
        isEqual(sessionFiltersAndSorting?.persistentSorting, sorting);

    // Count only filters whose column exists on this table; stale filters from sibling tabs
    // shouldn't reserve visual space here.
    const hasVisibleFilters = columnFilters.some((f) => isPresent(table.getColumn(f.id)));

    if (!hasVisibleFilters && sorting.length === 0) {
        return <></>;
    }

    return (
        <Flexbox
            gap={'32px'}
            sx={{
                justifyContent: 'space-between',
                paddingRight: '8px',
            }}
        >
            <FilterChips table={table} />
            {enableSaveAsDefault && hasEqualFiltersAndSorting && (
                <Tooltip title={t`Clear saved filter and sort settings for this session.`} variant="white">
                    <TertiaryButton
                        size={'small'}
                        onClick={handleReset}
                        startIcon={<BookmarkIcon fill="currentColor" />}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {t`Remove default`}
                    </TertiaryButton>
                </Tooltip>
            )}
            {enableSaveAsDefault && !hasEqualFiltersAndSorting && (
                <Tooltip title={t`Save filter and sort settings for this browsing session.`} variant="white">
                    <TertiaryButton
                        size={'small'}
                        onClick={handleClick}
                        startIcon={<BookmarkIcon />}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {t`Save as default`}
                    </TertiaryButton>
                </Tooltip>
            )}
        </Flexbox>
    );
}

export function FilterChips<TData extends RowData>({ table }: TanStackTableProps<TData, unknown>): JSX.Element {
    const { columnFilters, sorting } = useSelector(
        table.store,
        (state) => ({ columnFilters: state.columnFilters, sorting: state.sorting }),
        { compare: shallow },
    );
    const [menuState, setMenuState] = React.useState<FilterMenuState<TData>>({ type: 'None', anchorEl: undefined });

    // Only render chips for filters whose column actually exists on the current table.
    // Filters for columns absent here (e.g. persisted from a sibling tab with a different column
    // set) stay in state but are invisible — they re-appear on tabs that do have the column.
    // Also hide date filters that are selected but have no value yet.
    const visibleColumnFilters = columnFilters.filter((f) => {
        if (!isPresent(table.getColumn(f.id))) return false;
        if (
            isFilterValue(f.value) &&
            (f.value.filterFn === SupportedFilterFn.isInLastDays ||
                f.value.filterFn === SupportedFilterFn.isOlderThanDays) &&
            f.value.value === null
        ) {
            return false;
        }
        if (
            isFilterValue(f.value) &&
            f.value.filterFn === SupportedFilterFn.inDateRange &&
            Array.isArray(f.value.value) &&
            !isPresent(f.value.value[0]) &&
            !isPresent(f.value.value[1])
        ) {
            return false;
        }
        return true;
    });

    // Closes the filter menu once its column's filter is removed.
    React.useEffect(() => {
        if (menuState.type === 'ColumnFilter' && !columnFilters.some((filter) => filter.id === menuState.column.id)) {
            // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
            setMenuState({ type: 'None', anchorEl: undefined });
        }
    }, [columnFilters, menuState]);

    const handleOpenColumnFilter = (e: React.MouseEvent<HTMLDivElement>, column: Column<TData, unknown>) => {
        setMenuState({ type: 'ColumnFilter', anchorEl: e.currentTarget, column });
    };

    const handleClose = () => {
        setMenuState({ type: 'None', anchorEl: undefined });
    };

    if (visibleColumnFilters.length === 0 && sorting.length === 0) {
        return <></>;
    }

    return (
        <Flexbox
            gap={4}
            sx={{
                flexWrap: 'wrap',
            }}
        >
            {visibleColumnFilters.map((columnFilter) => {
                const column = table.getColumn(columnFilter.id);
                if (!column) return null;
                return (
                    <Chip
                        key={columnFilter.id}
                        color={'primary'}
                        style={{
                            maxWidth: '300px',
                            borderRadius: '4px',
                            border: `1px solid ${colorSystem.primary[2]}`,
                            backgroundColor: colorSystem.primary[1],
                        }}
                        onClick={(e) => handleOpenColumnFilter(e, column)}
                        onDelete={() => column.setFilterValue(undefined)}
                        label={renderFilterChipLabel({ columnFilter, table })}
                    />
                );
            })}
            <ColumFilterMenu menuState={menuState} onClose={handleClose} table={table} />
        </Flexbox>
    );
}

function getFilterChipColumnLabel<TData extends RowData>(
    column: { columnDef: { meta?: any } },
    table: Table<TData>,
): string {
    const rawLabel = column.columnDef.meta?.label?.() ?? t`Unknown`;
    const parentColumnId = column.columnDef.meta?.parentColumnId;
    if (parentColumnId) {
        const parentCol = table.getColumn(parentColumnId);
        const parentLabel = parentCol?.columnDef.meta?.label?.();
        if (parentLabel) {
            return `${parentLabel} > ${rawLabel}`;
        }
    }
    return rawLabel;
}

function renderFilterChipLabel<TData extends RowData>({
    columnFilter,
    table,
}: {
    columnFilter: ColumnFilter;
    table: Table<TData>;
}): JSX.Element {
    const column = table.getColumn(columnFilter.id);
    if (!isPresent(column)) {
        return <></>;
    }
    const filterValue = columnFilter.value;
    if (!isFilterValue(filterValue)) {
        return <></>;
    }
    const chipLabel = getFilterChipColumnLabel(column, table);

    if (filterValue.filterFn === SupportedFilterFn.includesString) {
        return (
            <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                <b>{chipLabel}</b>: {filterValue.value}
            </Text>
        );
    }

    if (filterValue.filterFn === SupportedFilterFn.isEmptyString) {
        return (
            <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                <b>{chipLabel}</b> {t`is empty`}
            </Text>
        );
    }

    if (filterValue.filterFn === SupportedFilterFn.isNotEmptyString) {
        return (
            <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                <b>{chipLabel}</b> {t`is not empty`}
            </Text>
        );
    }

    if (
        filterValue.filterFn === SupportedFilterFn.inNumberRange ||
        filterValue.filterFn === SupportedFilterFn.inMonetaryValueRange ||
        filterValue.filterFn === SupportedFilterFn.inQuantityRange
    ) {
        const [minValue, maxValue] = filterValue.value;

        if (isPresent(minValue) && isPresent(maxValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: {formatDecimal(minValue)} - {formatDecimal(maxValue)}
                </Text>
            );
        }

        if (isPresent(minValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: ≥ {formatDecimal(minValue)}
                </Text>
            );
        }

        if (isPresent(maxValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: ≤ {formatDecimal(maxValue)}
                </Text>
            );
        }
    }

    if (filterValue.filterFn === SupportedFilterFn.inDateRange) {
        const [minValue, maxValue] = filterValue.value;

        if (isPresent(minValue) && isPresent(maxValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: {formatToLongDate(minValue)} - {formatToLongDate(maxValue)}
                </Text>
            );
        }

        if (isPresent(minValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: ≥ {formatToLongDate(minValue)}
                </Text>
            );
        }
        if (isPresent(maxValue)) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: ≤ {formatToLongDate(maxValue)}
                </Text>
            );
        }

        // No bounds set yet (e.g. just switched to "is between"): render no chip.
        return <></>;
    }

    if (
        filterValue.filterFn === SupportedFilterFn.isInLastDays ||
        filterValue.filterFn === SupportedFilterFn.isOlderThanDays
    ) {
        const days = filterValue.value as RelativeDateFilterValue;
        if (days === null) {
            return <></>;
        }
        const fnLabel = transEnum(filterValue.filterFn, FilterFnOptionTranslations);
        const label = formatRelativeDateFilterLabel(days);
        return (
            <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                <b>{chipLabel}</b> {fnLabel} {label}
            </Text>
        );
    }

    const enumFilterConfig = getFilterConfig(column, 'enum');
    if (isPresent(enumFilterConfig) && Array.isArray(filterValue.value)) {
        const value = filterValue.value;
        if (filterValue.filterFn === SupportedFilterFn.equalsAny) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>: {value.map(enumFilterConfig.getOptionLabel as any).join(', ')}
                </Text>
            );
        }
        if (filterValue.filterFn === SupportedFilterFn.equalsNone) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>:{' Not '}
                    {value.map(enumFilterConfig.getOptionLabel as any).join(', ')}
                </Text>
            );
        }
    }

    const arrayFilterConfig = getFilterConfig(column, 'array');
    if (isPresent(arrayFilterConfig) && Array.isArray(filterValue.value)) {
        const value = filterValue.value;
        if (filterValue.filterFn === SupportedFilterFn.arrIncludesSome) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>:{' '}
                    {value.length > 0 ? value.map(arrayFilterConfig.getOptionLabel as any).join(', ') : t`-`}
                </Text>
            );
        }
        if (filterValue.filterFn === SupportedFilterFn.arrIncludesAll) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>:{' '}
                    {value.length > 0 ? value.map(arrayFilterConfig.getOptionLabel as any).join(', ') : t`-`}
                </Text>
            );
        }
        if (filterValue.filterFn === SupportedFilterFn.arrExcludesSome) {
            return (
                <Text variant={'body-small'} showEllipsis={true} style={{ color: colorSystem.primary[8] }}>
                    <b>{chipLabel}</b>:{' Not '}
                    {value.length > 0 ? value.map(arrayFilterConfig.getOptionLabel as any).join(', ') : t`-`}
                </Text>
            );
        }
    }

    throwErrorUnlessProduction(new Error(`Unsupported filter type: ${filterValue.filterFn}`));
    return <></>;
}
